/**
 * Les barils (author's addition): thrown like a grenade, a barrel rolls from where it lands, down the slopes too, and
 * leaves a big trail of what it holds: acid, or black powder (a fuse). It stays BARREL_TURNS turns. Hurt, it gives its
 * content away: the acid barrel sprays drops of acid all around, the powder barrel blows up. An explosion, a bullet, a
 * ray, a shell, fire and lava all hurt it. The conveyor of the Nuclear Plant brings acid barrels of its own (maps/events.ts).
 */
import { Physique, STATE_LIQUID, nextInt, cosDeg, sinDeg, wakeRect, TRIG_ONE } from '@invaders/sim';
import { FP, FP_SHIFT, PFP, PFP_SHIFT, TICKS_PER_SECOND, type Barrel, type GameState } from './state';
import { PROJ_ACID_DROP, WEAPON_ACID_BARREL } from './weapon-ids';
import { dropSand } from './projectile';
import { explode } from './explosion';
import { ownerPlayer } from './unit';
import { unitAt } from './weapons';

const { ACID, BLACK_POWDER, LAVA, FIRE } = Physique;

/** Its box: BARREL_SIZE cells a side, around its column, its last row on the ground. */
export const BARREL_SIZE = 10;
const HALF = BARREL_SIZE >> 1;
/** GDD-style: it lasts this many turns; in the sandbox (no turns), this many ticks. */
export const BARREL_TURNS = 3;
export const BARREL_SANDBOX_TICKS = 15 * TICKS_PER_SECOND;
/** Cells it holds, and cells it leaves per column rolled: about 300 columns of a thick trail, more than it rolls on flat ground. Values chosen. */
export const BARREL_LOAD = 2400;
const TRAIL_CELLS = 8;
/** Columns between the barrel's back and its trail. */
const TRAIL_GAP = 6;
/**
 * Rolling speed (fixed-point FP): it always gets going, whatever the throw's power, and loses about 1/96 of it per tick:
 * faster than its acid flows, which would dig under it and trap it in its own ditch.
 */
const ROLL_MIN = 2 * FP;
const ROLL_MAX = 3 * FP;
const ROLL_STOP = FP >> 3;
/** Speed gained per tick and per row of slope under it; the highest step it rolls up. */
const ROLL_SLOPE = 3;
const ROLL_STEP = 6;
const GRAVITY = 16;
const MAX_FALL = 6 * FP;
/** The powder barrel's explosion. Values chosen. */
export const POWDER_BARREL_RADIUS = 26;
export const POWDER_BARREL_DAMAGE = 120;
/** The acid barrel's burst: a splash where it stood, and drops thrown all around (2 to 5 cells per tick). */
export const ACID_BURST_RADIUS = 6;
export const ACID_BURST_DROPS = 20;

const free = (state: GameState, x: number, y: number): boolean => {
  const { w, h, data } = state.world.physique;
  if (x < 0 || x >= w || y >= h) return false;
  return y < 0 || state.world.rules.materials.state[data[y * w + x]] >= STATE_LIQUID;
};

/** The box of a barrel on column cx, its last row `by`, is free of solids and powders. */
function fits(state: GameState, cx: number, by: number): boolean {
  for (let y = by - BARREL_SIZE + 1; y <= by; y++) for (let x = cx - HALF; x < cx + HALF; x++) if (!free(state, x, y)) return false;
  return true;
}

/** A barrel on (cx, by), with its speed along the ground: it starts rolling at once, the way it was thrown. */
export function spawnBarrel(state: GameState, weapon: number, cx: number, by: number, vx: number, owner: number, load = BARREL_LOAD): void {
  let y = by;
  for (let up = 0; up < 2 * BARREL_SIZE && !fits(state, cx, y); up++) y--;
  state.barrels.push({ weapon, x: (cx << FP_SHIFT) + (FP >> 1), y: y << FP_SHIFT, vx, vy: 0, owner, turns: BARREL_TURNS, tick: 0, load });
}

/** Rolling speed of a barrel thrown at `pvx` (fixed-point PFP). */
export function rollSpeed(pvx: number): number {
  const v = Math.trunc(pvx / (PFP >> FP_SHIFT));
  const speed = Math.min(ROLL_MAX, Math.max(ROLL_MIN, Math.abs(v)));
  return v < 0 ? -speed : speed;
}

/** It is a thrown barrel's flight that ends here: the barrel lands, last free cell (cx, cy), and rolls. */
export function landBarrel(state: GameState, weapon: number, cx: number, cy: number, pvx: number, owner: number): void {
  spawnBarrel(state, weapon, cx, cy, rollSpeed(pvx), owner);
}

/** First row of ground under (x, from), at most 2 × BARREL_SIZE rows down. Its own trail is not ground: it would push it on. */
function groundRow(state: GameState, x: number, from: number): number {
  const { w, data } = state.world.physique;
  let y = from - ROLL_STEP;
  while (y < from + 2 * BARREL_SIZE && (free(state, x, y) || data[y * w + x] === BLACK_POWDER)) y++;
  return y;
}

/** The trail: TRAIL_CELLS cells behind the barrel, from the ground up, in what is empty, never in a unit. */
function spill(state: GameState, b: Barrel, x: number, by: number): void {
  if (b.load <= 0) return;
  const { w, data } = state.world.physique;
  const cells = state.world.cellState.data;
  const matState = state.world.rules.materials.state;
  if (x < 0 || x >= w) return;
  const mat = b.weapon === WEAPON_ACID_BARREL ? ACID : BLACK_POWDER;
  for (let y = by, n = 0; n < TRAIL_CELLS && y >= 0 && b.load > 0; y--, n++) {
    const i = y * w + x;
    if (matState[data[i]] <= STATE_LIQUID || unitAt(state, x, y, -1)) continue;
    data[i] = mat;
    cells[i] = 0;
    b.load--;
  }
  wakeRect(state.world, x - 1, by - TRAIL_CELLS, x + 1, by + 1);
}

/** Fire or lava against the barrel. */
function burning(state: GameState, cx: number, by: number): boolean {
  const { w, h, data } = state.world.physique;
  for (let y = Math.max(0, by - BARREL_SIZE); y <= Math.min(h - 1, by + 1); y++) {
    for (let x = Math.max(0, cx - HALF - 1); x <= Math.min(w - 1, cx + HALF); x++) {
      const c = data[y * w + x];
      if (c === LAVA || c === FIRE) return true;
    }
  }
  return false;
}

/** The barrel is hurt: gone, and what it holds with it; a blow of its owner's team (friendly fire). */
export function burstBarrel(state: GameState, index: number): void {
  const b = state.barrels[index];
  state.barrels.splice(index, 1);
  const source = state.source;
  state.source = ownerPlayer(state, b.owner);
  const cx = b.x >> FP_SHIFT;
  const cy = (b.y >> FP_SHIFT) - HALF;
  if (b.weapon === WEAPON_ACID_BARREL) {
    const rng = state.world.rng;
    dropSand(state, cx, cy, ACID_BURST_RADIUS, ACID);
    for (let k = 0; k < ACID_BURST_DROPS; k++) {
      const angle = 15 + nextInt(rng, 151);
      const speed = ((4 + nextInt(rng, 7)) * PFP) >> 1;
      state.projectiles.push({
        x: (cx << PFP_SHIFT) + (PFP >> 1), y: ((cy - HALF - 1) << PFP_SHIFT) + (PFP >> 1),
        vx: Math.trunc((speed * cosDeg(angle)) / TRIG_ONE), vy: -Math.trunc((speed * sinDeg(angle)) / TRIG_ONE),
        weapon: PROJ_ACID_DROP, owner: b.owner, age: 0, link: -1,
      });
    }
    state.events.push({ type: 'explosion', x: cx, y: cy, radius: 0 });
  } else explode(state, cx, cy, POWDER_BARREL_RADIUS, POWDER_BARREL_DAMAGE);
  state.source = source;
}

/** Start of a turn (match): every barrel has one turn less, and leaves after the last. */
export function barrelsTurnStart(state: GameState): void {
  for (let i = state.barrels.length - 1; i >= 0; i--) if (--state.barrels[i].turns <= 0) state.barrels.splice(i, 1);
}

/** One barrel's tick; false once it left the map. */
function roll(state: GameState, b: Barrel): boolean {
  const w = state.world.physique.w;
  let cx = b.x >> FP_SHIFT;
  let by = b.y >> FP_SHIFT;
  // Buried (sand, a remote's square): it comes back up.
  if (!fits(state, cx, by)) { by--; b.y -= FP; }
  const onGround = !fits(state, cx, by + 1);
  const rolling = b.vx !== 0;
  const { data } = state.world.physique;
  const wet = by >= HALF && state.world.rules.materials.state[data[(by - HALF) * w + cx]] === STATE_LIQUID;
  const belt = onGround ? state.map.belts.find((t) => by + 1 === t.y && cx >= t.x0 && cx <= t.x1) : undefined;
  const carried = belt !== undefined;
  if (belt) b.vx = belt.dir * belt.speed;
  else if (wet) {
    // In a liquid it hardly moves: it stops where it sank, it does not run along the sea floor.
    b.vx -= Math.trunc(b.vx / 8) + Math.sign(b.vx);
    if (Math.abs(b.vx) < ROLL_STOP) b.vx = 0;
  }
  if (onGround && !carried && !wet) {
    b.vy = 0;
    const slope = groundRow(state, cx + HALF + 1, by) - groundRow(state, cx - HALF - 1, by);
    if (Math.abs(slope) >= 2) b.vx += slope * ROLL_SLOPE;
    b.vx -= Math.trunc(b.vx / 96) + Math.sign(b.vx);
    if (Math.abs(b.vx) < ROLL_STOP && Math.abs(slope) < 2) b.vx = 0;
    b.vx = Math.max(-ROLL_MAX, Math.min(ROLL_MAX, b.vx));
  } else if (onGround) b.vy = 0;
  else {
    b.vy = Math.min(MAX_FALL, b.vy + Math.trunc(GRAVITY / state.map.gravityDiv));
    if (wet) b.vy = Math.min(b.vy, FP);
    const target = b.y + b.vy;
    const last = target >> FP_SHIFT;
    while (by < last && fits(state, cx, by + 1)) by++;
    if (by === last) b.y = target;
    else { b.y = by << FP_SHIFT; b.vy = 0; }
  }

  const target = b.x + b.vx;
  const last = target >> FP_SHIFT;
  const dir = b.vx < 0 ? -1 : 1;
  while (cx !== last) {
    const next = cx + dir;
    if (next < HALF || next >= w - HALF) return false;
    if (unitAt(state, next + dir * HALF, by - HALF, -1)) { b.vx = 0; break; }
    let up = 0;
    while (up <= ROLL_STEP && !fits(state, next, by - up)) up++;
    if (up > ROLL_STEP) {
      b.vx = -Math.trunc(b.vx / 3); // a wall: it comes back a little
      if (Math.abs(b.vx) < ROLL_STOP) b.vx = 0;
      break;
    }
    by -= up;
    if (onGround) for (let down = 0; down < ROLL_STEP && fits(state, next, by + 1); down++) by++;
    cx = next;
    if (onGround && !carried) spill(state, b, cx - dir * (HALF + TRAIL_GAP), by);
  }
  b.x = cx === last ? target : (cx << FP_SHIFT) + (FP >> 1);
  // Come to rest, it closes the gap between its trail and itself: a fuse must lead to the barrel.
  if (rolling && b.vx === 0 && onGround && !carried) for (let d = TRAIL_GAP - 1; d >= 0; d--) spill(state, b, cx - dir * (HALF + d), by);
  if (onGround) b.y = by << FP_SHIFT;
  else b.y = Math.min(b.y, (by << FP_SHIFT) + FP - 1);
  return true;
}

export function updateBarrels(state: GameState): void {
  for (let i = state.barrels.length - 1; i >= 0; i--) {
    if (i >= state.barrels.length) continue; // a chain reaction removed some
    const b = state.barrels[i];
    if (!roll(state, b)) { state.barrels.splice(i, 1); continue; }
    if (burning(state, b.x >> FP_SHIFT, b.y >> FP_SHIFT)) { burstBarrel(state, i); continue; }
    if (state.match === null && ++b.tick >= BARREL_SANDBOX_TICKS) state.barrels.splice(i, 1);
  }
}

/** Index of the barrel covering cell (x, y), or -1. */
export function barrelAt(state: GameState, x: number, y: number): number {
  for (let i = 0; i < state.barrels.length; i++) {
    const b = state.barrels[i];
    const cx = b.x >> FP_SHIFT;
    const by = b.y >> FP_SHIFT;
    if (x >= cx - HALF && x < cx + HALF && y > by - BARREL_SIZE && y <= by) return i;
  }
  return -1;
}

/** A bullet or a ray at cell (x, y): true if it hit a barrel. */
export function hitBarrel(state: GameState, x: number, y: number): boolean {
  const i = barrelAt(state, x, y);
  if (i < 0) return false;
  burstBarrel(state, i);
  return true;
}

/** An explosion hurts the barrels in its radius. */
export function blastBarrels(state: GameState, cx: number, cy: number, radius: number): void {
  const r = radius + HALF;
  for (let i = state.barrels.length - 1; i >= 0; i--) {
    if (i >= state.barrels.length) continue;
    const b = state.barrels[i];
    const dx = (b.x >> FP_SHIFT) - cx;
    const dy = (b.y >> FP_SHIFT) - HALF - cy;
    if (dx * dx + dy * dy <= r * r) burstBarrel(state, i);
  }
}

/** Still rolling or falling (the turn waits for the barrels the players threw, not for the conveyor's). */
export function barrelMoving(b: Barrel): boolean {
  return b.owner >= 0 && (b.vx !== 0 || b.vy !== 0);
}

